import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { Phone, Mail, MessageCircle, MapPin } from "lucide-react";
import { budgetRanges, locations } from "@/data/mock";

const Contact = () => {
  const { t, lang } = useApp();
  const [form, setForm] = useState({
    name: "",
    email: "",
    whatsapp: "",
    budget: "",
    location: "",
    time: "",
    message: "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success(t("contact_thanks"));
    setForm({
      name: "",
      email: "",
      whatsapp: "",
      budget: "",
      location: "",
      time: "",
      message: "",
    });
  };

  return (
    <main data-testid="contact-page" className="bg-[#0A0E1A] pt-32 pb-20">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Intro + contact info */}
          <div className="lg:col-span-5">
            <p className="overline">{t("contact_overline")}</p>
            <h1 className="font-display text-4xl lg:text-6xl mt-4 leading-[1.05] text-[#F5F0E8]">
              {t("contact_title")}
            </h1>
            <p className="mt-6 text-[#A0A5B5] font-light leading-relaxed max-w-md">
              {t("contact_sub")}
            </p>

            <div className="mt-10 space-y-5">
              {[
                { Icon: Phone, label: "+971 50 123 4567", href: "tel:+971501234567", tid: "phone" },
                { Icon: Mail, label: "hello@maisonprive.ae", href: "mailto:hello@maisonprive.ae", tid: "email" },
                { Icon: MessageCircle, label: "WhatsApp Business", href: "https://wa.me/971501234567", tid: "whatsapp" },
                { Icon: MapPin, label: "Downtown Dubai, UAE", href: "#map", tid: "address" },
              ].map(({ Icon, label, href, tid }) => (
                <a
                  key={tid}
                  href={href}
                  data-testid={`contact-info-${tid}`}
                  className="flex items-center gap-4 group"
                >
                  <span className="w-11 h-11 border border-[#C9A84C]/30 flex items-center justify-center text-[#C9A84C] group-hover:bg-[#C9A84C] group-hover:text-[#0A0E1A] transition-colors">
                    <Icon className="w-4 h-4" />
                  </span>
                  <span className="text-[#F5F0E8]/85 font-light group-hover:text-[#C9A84C] transition-colors">
                    {label}
                  </span>
                </a>
              ))}
            </div>

            <div className="mt-10 border-t border-[#C9A84C]/15 pt-6">
              <p className="overline">{t("contact_available")}</p>
              <p className="mt-3 text-sm text-[#F5F0E8]/85 font-light">
                {t("contact_avail_list")}
              </p>
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            data-testid="consultation-form"
            className="lg:col-span-7 border border-[#C9A84C]/25 p-8 lg:p-12 bg-[#1E2235]/40 space-y-7"
          >
            <div className="grid sm:grid-cols-2 gap-7">
              <input
                type="text"
                required
                placeholder={t("contact_name")}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="contact-name"
                className="lux-input"
              />
              <input
                type="email"
                required
                placeholder={t("contact_email")}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="contact-email"
                className="lux-input"
              />
              <input
                type="tel"
                placeholder={t("contact_whatsapp")}
                value={form.whatsapp}
                onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                data-testid="contact-whatsapp"
                className="lux-input"
              />
              <input
                type="text"
                placeholder={t("contact_time")}
                value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value })}
                data-testid="contact-time"
                className="lux-input"
              />
              <select
                value={form.budget}
                onChange={(e) => setForm({ ...form, budget: e.target.value })}
                data-testid="contact-budget"
                className="lux-input cursor-pointer"
              >
                <option value="" className="bg-[#0A0E1A]">{t("contact_budget")}</option>
                {budgetRanges.map((b) => (
                  <option key={b.id} value={b.id} className="bg-[#0A0E1A]">
                    {lang === "ar" ? b.label_ar : b.label_en}
                  </option>
                ))}
              </select>
              <select
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                data-testid="contact-location"
                className="lux-input cursor-pointer"
              >
                <option value="" className="bg-[#0A0E1A]">{t("contact_pref_loc")}</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id} className="bg-[#0A0E1A]">
                    {lang === "ar" ? l.name_ar : l.name_en}
                  </option>
                ))}
              </select>
            </div>
            <textarea
              rows={4}
              placeholder={t("contact_message")}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              data-testid="contact-message"
              className="lux-input resize-none"
            />
            <button
              type="submit"
              data-testid="contact-submit"
              className="btn-gold inline-flex items-center gap-3 px-10 py-4 text-xs tracking-[0.22em] uppercase font-medium"
            >
              {t("contact_submit")}
            </button>
          </form>
        </div>

        {/* Map */}
        <div id="map" className="mt-20 aspect-[21/9] border border-[#C9A84C]/15 overflow-hidden">
          <iframe
            title="Office location"
            src="https://www.google.com/maps?q=Downtown+Dubai&output=embed"
            className="w-full h-full grayscale contrast-125 opacity-90"
            loading="lazy"
            data-testid="contact-map"
          />
        </div>
      </div>
    </main>
  );
};

export default Contact;